import React, { useState, useEffect } from 'react';
import { WifiOff, Lock } from 'lucide-react';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { useAuth } from '../contexts/AuthContext';
import { offlineDataCache, CACHE_KEYS } from '../utils/offlineDataCache';

export const OfflineBlock: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isPremium, setIsPremium] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkPremiumStatus = async () => {
      setIsChecking(true);
      const accountId = user?.currentAccountId || user?.accountId;
      
      if (!accountId) {
        setIsPremium(false);
        setIsChecking(false);
        return;
      }

      // Check cache first (instant check)
      const cached = offlineDataCache.get<any>(CACHE_KEYS.SUBSCRIPTION);
      if (cached) {
        setIsPremium(cached.plan === 'premium');
        setIsChecking(false);
        // If online, refresh subscription in background but don't wait
        if (navigator.onLine) {
          const token = localStorage.getItem('token');
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/subscriptions/current`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
            .then(res => res.json())
            .then(data => {
              if (data.success && data.data?.subscription) {
                const subscription = data.data.subscription;
                setIsPremium(subscription.plan === 'premium');
                offlineDataCache.set(CACHE_KEYS.SUBSCRIPTION, subscription);
              }
            })
            .catch(() => {
              // Ignore errors - use cached value
            });
        }
        return;
      }

      // No cache - need to fetch
      if (navigator.onLine) {
        // Try to fetch subscription if online
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/subscriptions/current`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data?.subscription) {
              const subscription = data.data.subscription;
              setIsPremium(subscription.plan === 'premium');
              offlineDataCache.set(CACHE_KEYS.SUBSCRIPTION, subscription);
            } else {
              setIsPremium(false);
            }
          } else {
            setIsPremium(false);
          }
        } catch (error) {
          console.error('Failed to fetch subscription:', error);
          setIsPremium(false);
        }
      } else {
        // Offline and no cache - assume free
        setIsPremium(false);
      }
      
      setIsChecking(false);
    };

    if (user) {
      checkPremiumStatus();
    } else {
      setIsPremium(false);
      setIsChecking(false);
    }
  }, [user]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Show blocking modal if offline and not premium
  // Don't show while checking if we're online (allow premium check to complete)
  if (!isOnline && !isChecking && !isPremium) {
    return (
      <div className="fixed inset-0 z-[9999] bg-gray-900 bg-opacity-95 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-8 text-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 bg-error-100 dark:bg-error-900/30 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-error-600 dark:text-error-400" />
            </div>
            <WifiOff className="w-12 h-12 text-gray-400 dark:text-gray-500" />
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {t('offline.blockedTitle') || 'Offline Mode Unavailable'}
            </h2>
            
            <p className="text-gray-600 dark:text-gray-400">
              {t('offline.blockedMessage') || 'Offline mode is only available for Premium accounts. Please reconnect to the internet to continue using the application.'}
            </p>
            
            <div className="mt-4 p-4 bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-sm text-warning-800 dark:text-warning-200">
                {t('offline.upgradePrompt') || 'Upgrade to Premium to enable offline functionality and continue working even without internet connection.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

